import React, { useState, useEffect } from 'react';
import { handleNumericInput, handleNumericKeyDown, processNumericInput } from '../utils/inputUtils';
import { Plus, Calendar, Edit2, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { NativeSelect } from './ui/native-select';
import axios from 'axios';

// URL centralizado que funciona em produção mesmo com .env errado
import { getBackendUrl } from '../utils/config';
const API_URL = getBackendUrl();

const RevendasTab = () => {
  const [revendas, setRevendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataFiltro, setDataFiltro] = useState(new Date().toISOString().split('T')[0]);
  const [showModal, setShowModal] = useState(false);
  const [editingRevenda, setEditingRevenda] = useState(null);
  
  // Novos filtros
  const [dataInicio, setDataInicio] = useState(new Date().toISOString().split('T')[0]);
  const [dataFim, setDataFim] = useState(new Date().toISOString().split('T')[0]);
  const [colaboradorFiltro, setColaboradorFiltro] = useState('');
  const [turnoFiltro, setTurnoFiltro] = useState('');
  const [colaboradores, setColaboradores] = useState([]);
  
  // Helper para selecionar todo o texto quando foca num input numérico
  const handleFocusSelect = (e) => {
    e.target.select();
    // Se o valor é 0, limpar o campo para facilitar a escrita
    if (e.target.value === '0' || e.target.value === '0.00') {
      setTimeout(() => {
        e.target.value = '';
        e.target.dispatchEvent(new Event('input', { bubbles: true }));
      }, 0);
    }
  };
  
  const [formData, setFormData] = useState({
    data: new Date().toISOString().split('T')[0],
    turno: 'Manhã',
    raspadinha_1: 0,
    raspadinha_2: 0,
    raspadinha_3: 0,
    raspadinha_5: 0,
    raspadinha_10: 0,
    raspadinha_15: 0,
    premios: 0,
    observacoes: ''
  });

  useEffect(() => {
    loadColaboradores();
    loadRevendas();
  }, [dataInicio, dataFim, colaboradorFiltro, turnoFiltro]);

  const loadColaboradores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setColaboradores(response.data.filter(u => u.ativo !== false && (u.role === 'colaborador' || u.role === 'responsavel')));
    } catch (err) {
      console.error('Erro ao carregar colaboradores:', err);
    }
  };

  const loadRevendas = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Buscar todas as revendas (backend filtra por permissões)
      const response = await axios.get(`${API_URL}/api/revendas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let revendasFiltradas = response.data;
      
      // Filtrar por intervalo de datas
      if (dataInicio && dataFim) {
        revendasFiltradas = revendasFiltradas.filter(r => 
          r.data >= dataInicio && r.data <= dataFim
        );
      } else if (dataInicio) {
        revendasFiltradas = revendasFiltradas.filter(r => 
          r.data >= dataInicio
        );
      }
      
      // Filtrar por colaborador
      if (colaboradorFiltro) {
        revendasFiltradas = revendasFiltradas.filter(r => 
          r.colaborador_nome === colaboradorFiltro
        );
      }
      
      // Filtrar por turno
      if (turnoFiltro) {
        revendasFiltradas = revendasFiltradas.filter(r => 
          r.turno === turnoFiltro
        );
      }
      
      setRevendas(revendasFiltradas);
      setError(null);
    } catch (err) {
      setError('Erro ao carregar revendas');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      // O backend espera raspadinha_1, raspadinha_2, premios (não vendidas_1, valor_premios)
      const payload = {
        data: formData.data,
        turno: formData.turno,
        raspadinha_1: formData.raspadinha_1 || 0,
        raspadinha_2: formData.raspadinha_2 || 0,
        raspadinha_3: formData.raspadinha_3 || 0,
        raspadinha_5: formData.raspadinha_5 || 0,
        raspadinha_10: formData.raspadinha_10 || 0,
        raspadinha_15: formData.raspadinha_15 || 0,
        premios: formData.premios || 0,
        observacoes: formData.observacoes || ''
      };
      
      if (editingRevenda) {
        await axios.put(
          `${API_URL}/api/revendas/${editingRevenda.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Se mudou a data, atualizar o filtro para a nova data
        if (formData.data !== dataFiltro) {
          setDataFiltro(formData.data);
        }
      } else {
        await axios.post(
          `${API_URL}/api/revendas`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setShowModal(false);
      resetForm();
      loadRevendas();
      
      // Disparar evento para notificar outros componentes (ex: StocksTab)
      window.dispatchEvent(new CustomEvent('stockModificado'));
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar revenda');
      console.error('Error saving revenda:', err);
    }
  };

  const handleEdit = (revenda) => {
    setEditingRevenda(revenda);
    setFormData(revenda);
    setShowModal(true);
  };

  const handleDelete = async (revendaId) => {
    if (!window.confirm('Tem certeza que deseja eliminar esta revenda? O stock será revertido.')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/revendas/${revendaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadRevendas();
      
      // Disparar evento para notificar outros componentes (ex: StocksTab)
      window.dispatchEvent(new CustomEvent('stockModificado'));
    } catch (err) {
      setError('Erro ao eliminar revenda');
    }
  };

  const resetForm = () => {
    setEditingRevenda(null);
    setFormData({
      data: dataFiltro,
      turno: 'Manhã',
      raspadinha_1: 0,
      raspadinha_2: 0,
      raspadinha_3: 0,
      raspadinha_5: 0,
      raspadinha_10: 0,
      raspadinha_15: 0,
      premios: 0,
      observacoes: ''
    });
  };

  const updateField = (field, value) => {
    setFormData({...formData, [field]: value});
  };

  const calcularTotal = () => {
    return (
      formData.raspadinha_1 * 1 +
      formData.raspadinha_2 * 2 +
      formData.raspadinha_3 * 3 +
      formData.raspadinha_5 * 5 +
      formData.raspadinha_10 * 10 +
      formData.raspadinha_15 * 15
    );
  };

  if (loading) return <div className="text-center py-8">Carregando...</div>;

  return (
    <div className="space-y-6">
      {/* Header com Filtros */}
      <Card className="bg-gradient-card border-primary/20">
        <CardContent className="p-4">
          <div className="space-y-4">
            {/* Linha 1: Filtros de Data */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-1 text-muted-foreground">Data Início</label>
                <input
                  type="date"
                  value={dataInicio}
                  onChange={(e) => setDataInicio(e.target.value)}
                  className="px-3 py-2 border rounded-md bg-background"
                />
              </div>
              
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-1 text-muted-foreground">Data Fim</label>
                <input
                  type="date"
                  value={dataFim}
                  onChange={(e) => setDataFim(e.target.value)}
                  className="px-3 py-2 border rounded-md bg-background"
                />
              </div>
              
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-1 text-muted-foreground">Colaborador</label>
                <select
                  value={colaboradorFiltro}
                  onChange={(e) => setColaboradorFiltro(e.target.value)}
                  className="px-3 py-2 border rounded-md bg-background"
                >
                  <option value="">Todos</option>
                  {colaboradores.map(colab => (
                    <option key={colab.id} value={colab.nome}>
                      {colab.nome}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="flex flex-col">
                <label className="text-sm font-medium mb-1 text-muted-foreground">Turno</label>
                <select
                  value={turnoFiltro}
                  onChange={(e) => setTurnoFiltro(e.target.value)}
                  className="px-3 py-2 border rounded-md bg-background"
                >
                  <option value="">Todos</option>
                  <option value="Manhã">Manhã</option>
                  <option value="Tarde">Tarde</option>
                </select>
              </div>
            </div>
            
            {/* Linha 2: Botão Adicionar */}
            <div className="flex justify-end">
              <Button 
                onClick={() => { resetForm(); setShowModal(true); }} 
                className="bg-primary hover:bg-primary/90"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova Revenda Raspadinha
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Lista de Revendas */}
      <div className="grid grid-cols-1 gap-4">
        {revendas.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-muted-foreground">
              <Plus className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhuma revenda de raspadinhas registrada nesta data</p>
            </CardContent>
          </Card>
        ) : (
          revendas.map((revenda) => (
            <Card key={revenda.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Turno {revenda.turno}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      {new Date(revenda.data + 'T00:00:00').toLocaleDateString('pt-PT')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleEdit(revenda)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="text-destructive hover:text-destructive" 
                      onClick={() => handleDelete(revenda.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  {[1, 2, 3, 5, 10, 15].map(valor => {
                    const qtd = revenda[`raspadinha_${valor}`];
                    if (qtd > 0) {
                      return (
                        <div key={valor} className="flex justify-between">
                          <span className="text-muted-foreground">{valor}€:</span>
                          <span className="font-semibold">{qtd} un. (€{(qtd * valor).toFixed(2)})</span>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
                <div className="mt-3 pt-3 border-t space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Quantidade Total:</span>
                    <span className="font-semibold">
                      {(
                        revenda.raspadinha_1 +
                        revenda.raspadinha_2 +
                        revenda.raspadinha_3 +
                        revenda.raspadinha_5 +
                        revenda.raspadinha_10 +
                        revenda.raspadinha_15
                      )} unidades
                    </span>
                  </div>
                  <div className="flex justify-between bg-primary/10 p-2 rounded">
                    <span className="font-semibold">Valor Total das Raspadinhas:</span>
                    <span className="font-bold text-primary">
                      €{(
                        revenda.raspadinha_1 * 1 +
                        revenda.raspadinha_2 * 2 +
                        revenda.raspadinha_3 * 3 +
                        revenda.raspadinha_5 * 5 +
                        revenda.raspadinha_10 * 10 +
                        revenda.raspadinha_15 * 15
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between bg-success/10 p-2 rounded border border-success/30">
                    <div>
                      <span className="font-semibold text-success">Desconto 5%:</span>
                      <p className="text-xs text-muted-foreground">(Comissão)</p>
                    </div>
                    <span className="font-bold text-success">
                      €{(
                        (revenda.raspadinha_1 * 1 +
                        revenda.raspadinha_2 * 2 +
                        revenda.raspadinha_3 * 3 +
                        revenda.raspadinha_5 * 5 +
                        revenda.raspadinha_10 * 10 +
                        revenda.raspadinha_15 * 15) * 0.05
                      ).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between bg-accent/10 p-2 rounded border border-accent/30">
                    <div>
                      <span className="font-semibold text-accent">Valor do Recibo (+23%):</span>
                      <p className="text-xs text-muted-foreground">(Comissão + 23%)</p>
                    </div>
                    <span className="font-bold text-accent">
                      €{(
                        (revenda.raspadinha_1 * 1 +
                        revenda.raspadinha_2 * 2 +
                        revenda.raspadinha_3 * 3 +
                        revenda.raspadinha_5 * 5 +
                        revenda.raspadinha_10 * 10 +
                        revenda.raspadinha_15 * 15) * 0.05 * 1.23
                      ).toFixed(2)}
                    </span>
                  </div>
                  {revenda.premios > 0 && (
                    <div className="flex justify-between text-sm text-warning">
                      <span className="font-semibold">Prémios:</span>
                      <span className="font-semibold">-€{revenda.premios.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="mt-2 pt-2 border-t border-primary/30">
                    <div className="flex justify-between items-center bg-primary/20 p-2 rounded">
                      <span className="font-bold text-primary">Valor Total Líquido:</span>
                      <span className="font-bold text-xl text-primary">
                        €{(() => {
                          const total = (
                            revenda.raspadinha_1 * 1 +
                            revenda.raspadinha_2 * 2 +
                            revenda.raspadinha_3 * 3 +
                            revenda.raspadinha_5 * 5 +
                            revenda.raspadinha_10 * 10 +
                            revenda.raspadinha_15 * 15
                          );
                          const desconto = total * 0.05;
                          const premios = revenda.premios || 0;
                          return (total - desconto - premios).toFixed(2);
                        })()}
                      </span>
                    </div>
                  </div>
                </div>
                {revenda.observacoes && (
                  <div className="mt-2 text-sm text-muted-foreground">
                    <strong>Obs:</strong> {revenda.observacoes}
                  </div>
                )}
                <div className="mt-2 text-xs text-muted-foreground">
                  Colaborador: {revenda.colaborador_nome}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        >
          <div 
            className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-border sticky top-0 bg-card z-10">
              <h2 className="text-xl font-semibold flex items-center gap-2">
                <Plus className="h-5 w-5" />
                {editingRevenda ? 'Editar' : 'Nova'} Revenda de Raspadinhas
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Data e Turno */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Data</label>
                  <input
                    type="date"
                    value={formData.data}
                    onChange={(e) => updateField('data', e.target.value)}
                    className="w-full px-3 py-2 border rounded-md bg-background"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Turno</label>
                  <NativeSelect
                    value={formData.turno}
                    onChange={(e) => updateField('turno', e.target.value)}
                    className="w-full"
                    required
                  >
                    <option value="Manhã">Manhã</option>
                    <option value="Tarde">Tarde</option>
                  </NativeSelect>
                </div>
              </div>

              {/* Raspadinhas */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Quantidades Vendidas</CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Estas quantidades serão descontadas do stock do turno selecionado
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {[
                      { valor: 1, label: '1€' },
                      { valor: 2, label: '2€' },
                      { valor: 3, label: '3€' },
                      { valor: 5, label: '5€' },
                      { valor: 10, label: '10€' },
                      { valor: 15, label: '15€' }
                    ].map(({ valor, label }) => (
                      <div key={valor}>
                        <label className="block text-sm mb-2">{label}</label>
                        <input
                          type="text" inputMode="decimal"
                          min="0"
                          value={formData[`raspadinha_${valor}`]}
                          onChange={(e) => updateField(`raspadinha_${valor}`, e.target.value === '' ? '' : parseInt(e.target.value.replace(',', '.')) || 0)}
                          onFocus={handleFocusSelect}
                          onKeyDown={handleNumericKeyDown}
                          className="w-full px-3 py-2 border rounded-md bg-background"
                        />
                      </div>
                    ))}
                  </div>
                  
                  {/* Totais */}
                  <div className="mt-4 pt-4 border-t space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Quantidade Total:</span>
                      <span className="font-semibold">
                        {(
                          formData.raspadinha_1 +
                          formData.raspadinha_2 +
                          formData.raspadinha_3 +
                          formData.raspadinha_5 +
                          formData.raspadinha_10 +
                          formData.raspadinha_15
                        )} unidades
                      </span>
                    </div>
                    <div className="flex justify-between items-center bg-primary/10 p-3 rounded-lg">
                      <span className="font-semibold text-lg">Valor Total das Raspadinhas:</span>
                      <span className="font-bold text-2xl text-primary">
                        €{calcularTotal().toFixed(2)}
                      </span>
                    </div>
                    
                    {/* Prémios - logo após Valor Total das Raspadinhas */}
                    <div className="bg-muted/50 p-4 rounded-lg border border-border">
                      <div className="mb-3">
                        <span className="font-semibold text-base">Prémios</span>
                        <p className="text-xs text-muted-foreground mt-1">
                          Valor dos prémios pagos nesta revenda
                        </p>
                      </div>
                      <div>
                        <label className="block text-sm mb-2 font-medium">Valor dos Prémios (€)</label>
                        <input
                          type="text" inputMode="decimal"
                          min="0"
                          value={formData.premios}
                          onChange={(e) => updateField('premios', e.target.value === '' ? '' : parseFloat(e.target.value.replace(',', '.')) || 0)}
                          onFocus={handleFocusSelect}
                          onKeyDown={handleNumericKeyDown}
                          className="w-full px-3 py-2 border rounded-md bg-background"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center bg-success/10 p-3 rounded-lg border border-success/30">
                      <div>
                        <span className="font-semibold text-lg text-success">Desconto 5%:</span>
                        <p className="text-xs text-muted-foreground mt-1">
                          (Valor da comissão)
                        </p>
                      </div>
                      <span className="font-bold text-2xl text-success">
                        €{(calcularTotal() * 0.05).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center bg-accent/10 p-3 rounded-lg border border-accent/30">
                      <div>
                        <span className="font-semibold text-lg text-accent">Valor do Recibo (+23%):</span>
                        <p className="text-xs text-muted-foreground mt-1">
                          (Comissão + 23%)
                        </p>
                      </div>
                      <span className="font-bold text-2xl text-accent">
                        €{(calcularTotal() * 0.05 * 1.23).toFixed(2)}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center bg-primary/10 p-3 rounded-lg border-2 border-primary">
                      <span className="font-bold text-lg">Valor Total Líquido:</span>
                      <span className="font-bold text-2xl text-primary">
                        €{(calcularTotal() - (calcularTotal() * 0.05) - formData.premios).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Observações */}
              <div>
                <label className="block text-sm font-medium mb-2">Observações (opcional)</label>
                <textarea
                  value={formData.observacoes}
                  onChange={(e) => updateField('observacoes', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md bg-background"
                  rows={3}
                  placeholder="Notas adicionais sobre esta revenda..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); resetForm(); }}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90">
                  {editingRevenda ? 'Atualizar' : 'Salvar'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RevendasTab;
